<template>
  <Modal
    title=""
    :is-open="isOpen"
    data-testid="change-background-modal"
    :size="modalSize"
    @close="emit('close')"
  >
    <div
      v-show="modalStage === 1"
      class="change-background__stage-one"
      data-testid="stage-one"
    >
      <Heading :level="3" class="change-background__stage-one__title"
        >Imagens do Unsplash</Heading
      >
      <InputText
        v-model="imageInput"
        class="change-background__stage-one__input"
        label="Buscar"
        @input="fetchWithDebounce()"
      />
      <div
        ref="scrollContainer"
        class="change-background__stage-one__container"
      >
        <InfiniteScroll
          ref="infiniteScroll"
          :key="updateKey"
          :max-per-page="6"
          :request="() => fetchPortfolioImages(false)"
          :item-list="images"
          :whitelabel="true"
          data-testid="infinite-scroll"
        >
          <template #default="item">
            <div class="change-background__stage-one__content">
              <BaseCard
                class="change-background__stage-one__image"
                :background="item.urls.small"
                :title="item.user.name"
                data-testid="image"
                @click="changeModalStage(2, item)"
              />
            </div>
          </template>
        </InfiniteScroll>
      </div>
    </div>
    <div
      v-show="modalStage === 2"
      class="change-background__stage-two"
      data-testid="stage-two"
    >
      <Heading :level="3" class="change-background__stage-two__title"
        >Alterar imagem da capa</Heading
      >
      <div class="change-background__stage-two__chosen-img">
        <img
          v-if="chosenImage"
          :src="chosenImage.urls.small"
          :alt="chosenImage.user.name"
        />
        <BaseText
          class="change-background__stage-two__chosen-img__portfolio-name"
          :color="NebraskaColors.elementOverImage"
          size="p3"
        >
          {{ portfolioName }}
        </BaseText>
      </div>
      <ButtonContainer>
        <template #button-primary>
          <ButtonPrimary
            label="Salvar"
            data-testid="save-button"
            @click="changeModalBackgroundImage(chosenImage)"
          />
        </template>
        <template #button-secondary>
          <ButtonSecondary
            data-testid="back-button"
            label="Voltar"
            @click="changeModalStage(1, null)"
          />
        </template>
      </ButtonContainer>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import {
  Modal,
  InputText,
  Heading,
  InfiniteScroll,
  BaseText,
  ButtonPrimary,
  ButtonSecondary,
  ButtonContainer
} from '@warrenbrasil/nebraska-web';
import {
  getBackgroundImages,
  putBackgroundImage,
  IPhotos
} from '@/modules/wealth/services/portfolio';
import { BaseCard } from '@/modules/common/components/cards/BaseCard';
import { debounce } from 'lodash-es';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  portfolioId: { type: String, required: true },
  portfolioName: { type: String, required: true }
});
const emit = defineEmits(['close', 'reload']);

const imageInput = ref('');
const images = ref<IPhotos[]>([]);
const page = ref(1);
const modalStage = ref(1);
const chosenImage = ref<IPhotos | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const modalSize = computed(() => (modalStage.value === 1 ? 'md' : 'sm'));
const updateKey = ref(0);
const scrollTopPosition = ref(0);

const fetchWithDebounce = debounce(() => {
  fetchPortfolioImages(true);
  updateKey.value++;
}, 500);
async function fetchPortfolioImages(restartPage: boolean) {
  const queryInfo = {
    query: imageInput.value,
    page: page.value,
    perPage: 10
  };
  const { photos } = await getBackgroundImages(queryInfo);
  if (restartPage) {
    images.value = [];
    page.value = 1;
    scrollContainer?.value && (scrollContainer.value.scrollTop = 0);
  } else {
    page.value += 1;
  }

  const existingImageIds = new Set(images.value.map(image => image.id));
  const uniquePhotos = photos.filter(photo => !existingImageIds.has(photo.id));

  uniquePhotos.forEach(photo => images.value.push(photo));
}
async function changeModalBackgroundImage(photo: IPhotos | null) {
  if (photo) {
    await putBackgroundImage(props.portfolioId, photo.urls);
    modalStage.value = 1;
    emit('close');
    emit('reload');
  }
}

function changeModalStage(stage: number, photo: IPhotos | null) {
  if (stage === 2 && scrollContainer.value) {
    scrollTopPosition.value = scrollContainer.value.scrollTop;
  }
  modalStage.value = stage;
  chosenImage.value = photo;
  nextTick(() => {
    if (stage === 1 && scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollTopPosition.value;
    }
  });
}
</script>

<style lang="less" scoped>
:deep(.base-modal--wrapper.size-md) {
  max-height: 90vh;
  max-width: 590px;
}

:deep(.base-modal--wrapper.size-sm) {
  max-width: 504px;
}

:deep(.modal-content) {
  padding: 0;
}

:deep(.card) {
  box-shadow: none !important;

  &:hover {
    box-shadow: none !important;
  }
}

:deep(.loader.custom) {
  background-color: @background-secondary !important;
}

.change-background {
  &__stage-one,
  &__stage-two {
    width: 100%;

    &__title {
      color: @element-primary;
      margin-bottom: @size-spacing-x600;
      text-align: start;
      width: 100%;
    }
  }

  &__stage-one {
    padding: 0 calc(@size-spacing-x1000 - @size-spacing-x300)
      @size-spacing-x1000;

    &__title {
      padding: 0 @size-spacing-x300;
    }

    &__input {
      width: 100%;
      margin-bottom: @size-spacing-x600;
      padding: 0 @size-spacing-x300;
    }

    &__container {
      width: 100%;
      height: 552px;

      @media screen and (max-height: 900px) and (min-width: 500px) {
        height: 220px;
      }

      overflow-y: scroll;
      &::-webkit-scrollbar {
        width: 12px;
      }

      &::-webkit-scrollbar-track {
        background: @background-secondary;
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgb(199, 199, 199);
        border-radius: @size-radius-x400;
        border: 3px solid @background-secondary;
      }
    }

    :deep(.container-items) {
      overflow: hidden;
      padding: @size-spacing-x300;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: @size-spacing-x300;
    }

    :deep(.container-items::-webkit-scrollbar) {
      display: none;
    }

    &__image {
      display: flex;
      width: 100%;
      height: 179px;
      align-items: stretch;
      border-radius: @size-radius-x200;
    }
  }

  &__stage-two {
    &__title {
      padding: 0 @size-spacing-x1000;
    }

    &__chosen-img {
      padding: 0 @size-spacing-x1000 @size-spacing-x1000 @size-spacing-x1000;
      width: 100%;
      border-radius: @size-radius-x200;
      position: relative;
      overflow: hidden;
      height: 200px;
      margin-bottom: @size-spacing-x600;

      img {
        border-radius: @size-radius-x200;
        width: 376px !important;
        height: 200px !important;
        object-fit: cover;
      }

      &__portfolio-name {
        position: absolute;
        bottom: @size-spacing-x500;
        overflow: hidden;
        left: calc(@size-spacing-x1000 + @size-spacing-x500);
        width: 100%;
        z-index: 2;
        text-align: left;
        max-width: calc(376px - (@size-spacing-x500 * 2));
      }

      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        max-width: 376px;
        border-radius: @size-radius-x200;
        margin: 0 @size-spacing-x1000;
        z-index: 1;
        background: linear-gradient(
          180deg,
          rgba(51, 49, 49, 0) 13%,
          rgb(0 0 0 / 65%) 82.75%
        );
      }

      span {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        padding: @size-spacing-x400;
        color: @element-primary;
        font-weight: 700;
        z-index: 2;
      }
    }
  }

  @media screen and (max-width: 500px) {
    &__stage-one {
      padding: 0 @size-spacing-x400 @size-spacing-x400 @size-spacing-x400;
    }

    &__stage-two {
      &__title {
        padding: 0 @size-spacing-x400;
      }

      &__chosen-img {
        display: flex;
        padding: 0 @size-spacing-x400 @size-spacing-x400 @size-spacing-x400;

        &__portfolio-name {
          left: calc(@size-spacing-x400 * 2);
        }

        &::after {
          margin: 0 @size-spacing-x400;
          width: calc(100% - (@size-spacing-x400 * 2));
        }
      }
    }
  }
}
</style>
